import type { Command } from "@imminently/decisively-core/utils";
import { Typography } from "@mui/material";
import cn from "clsx";
import camelCase from "lodash/camelCase";
import React from "react";

export const convertFunctionName = (value: string) => {
  const text = camelCase(value);
  return text[0].toUpperCase() + text.slice(1);
};

export const convertFunction = (command: Command) => {
  const { id, arguments: args } = command;
  return `${convertFunctionName(id)}(${args
    .reduce((acc, arg) => {
      if (arg.required) {
        acc.push(arg.name);
      }
      return acc;
    }, [] as string[])
    .join(", ")})`;
};

type FunctionDeclarationProps = {
  command: Command;
};

export const FunctionDeclaration = ({ command }: FunctionDeclarationProps) => {
  const { id, arguments: args } = command;
  return (
    <>
      <span>{convertFunctionName(id)}</span>(
      {args.map((arg, i) => {
        const { name, required, variadic } = arg;
        const variadicString = variadic ? "..." : "";
        const optionalString = required ? "" : "?";

        return (
          <React.Fragment key={i}>
            {i > 0 && ", "}
            <span style={{ opacity: required ? 1 : 0.5 }}>{`${name}${optionalString}${variadicString}`}</span>
          </React.Fragment>
        );
      })}
      )
    </>
  );
};

// DEV NOTE going with `function` to refer to these, as I want to use
// `command` for the future command palette feature.

type FunctionInfoProps = {
  command: Command;
  className?: string;
};

/**
 * A component to display function name, usage and description.
 * Similar to an IDE's function hinting popup.
 */
export const FunctionInfo = ({ command, className }: FunctionInfoProps) => {
  if (!command) {
    return <Typography>Missing function data</Typography>;
  }
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <Typography>
        <FunctionDeclaration command={command} />
      </Typography>
      {command.description ? (
        <Typography
          variant="caption"
          style={{ fontStyle: "italic" }}
          dangerouslySetInnerHTML={{ __html: command.description }}
        />
      ) : null}
    </div>
  );
};
